/**
 * Adding a routing group, and linking a destination into one.
 *
 * Deliberately two small forms rather than one big "new group with
 * destinations" wizard. A group and its links are edited separately everywhere
 * else in this screen, and a create flow that works differently from the edit
 * flow is a second thing to learn for no gain.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Grid, Group, Modal, NumberInput, Select, Stack, Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useCampaignDetail, useCreateRouteGroup, useDestinations, useEnums,
  useLinkDestination,
} from '../api/queries';
import { formatGBP } from '../theme';
import { useVocab } from '../vocab';

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;
type RouteGroup = CampaignDetail['groups'][number];

export function NewGroupModal({
  campaignId,
  opened,
  existing,
  onClose,
}: {
  campaignId: string | undefined;
  opened: boolean;
  existing: RouteGroup[];
  onClose: () => void;
}) {
  const { data: enums } = useEnums();
  const create = useCreateRouteGroup(campaignId);
  const v = useVocab();

  // Default to one tier below the lowest-priority group, which is where a new
  // tier almost always belongs: a fallback for what the existing tiers reject.
  const nextPriority = existing.reduce((n, g) => Math.max(n, g.priority), 0) + 1;
  const [name, setName] = useState('');
  const [priority, setPriority] = useState<number>(nextPriority);
  const [method, setMethod] = useState('priority');

  async function submit() {
    try {
      await create.mutateAsync({
        name,
        priority,
        distribution_method: method,
        // Filters off by default. A new tier that silently screens leads is
        // harder to debug than one that takes everything and is then narrowed.
        filters_enabled: false,
      });
      notifications.show({
        color: 'teal',
        title: `${v.One('routeGroup')} created`,
        message: `${name} added as tier ${priority}. It has no ${v.many('destination')} yet, so it cannot deliver until you add one.`,
      });
      setName('');
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Create failed', message: (e as Error).message });
    }
  }

  const clash = existing.some((g) => g.priority === priority);

  return (
    <Modal opened={opened} onClose={onClose} title={`New ${v.one('routeGroup')}`} size="lg">
      <Stack>
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label={`${v.One('routeGroup')} name`}
              placeholder="Fallback"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 3 }}>
            <NumberInput
              label="Priority"
              description="Lower runs first"
              min={1}
              value={priority}
              onChange={(v) => setPriority(Number(v) || 1)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 3 }}>
            <Select
              label="Distribution"
              data={(enums?.distribution_methods ?? []).map((m) => ({ value: m.value, label: m.label }))}
              value={method}
              allowDeselect={false}
              onChange={(v) => v && setMethod(v)}
            />
          </Grid.Col>
        </Grid>

        {clash && (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">
              Another {v.one('routeGroup')} already sits at priority {priority}. Same-priority {v.many('routeGroup')} split traffic
              by their group weight rather than falling through to each other, which may not be what
              you want.
            </Text>
          </Alert>
        )}

        <Text size="xs" c="dimmed">
          {`The ${v.one('routeGroup')} is created with filters off and no ${v.many('destination')}. Add ${v.many('destination')} to it, then edit it to set caps and filters.`}
        </Text>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={create.isPending} disabled={!name.trim()} onClick={submit}>
            Create group
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function LinkDestinationModal({
  campaignId,
  group,
  onClose,
}: {
  campaignId: string | undefined;
  group: RouteGroup | null;
  onClose: () => void;
}) {
  const { data: all, isPending } = useDestinations();
  const link = useLinkDestination(campaignId);
  const v = useVocab();

  const [chosen, setChosen] = useState<string | null>(null);
  const [price, setPrice] = useState<number>(0);
  const [priority, setPriority] = useState<number>(1);

  // Only destinations not already in this group. Linking the same destination
  // twice would give the group two rows racing for the same endpoint.
  const options = useMemo(() => {
    if (!all || !group) return [];
    const already = new Set(group.destinations.map((d) => d.destination_id));
    return all.destinations.filter((d) => !already.has(d.id));
  }, [all, group]);

  const picked = options.find((d) => d.id === chosen);

  async function submit() {
    if (!group || !chosen) return;
    try {
      await link.mutateAsync({
        groupId: group.id,
        body: {
          destination_id: chosen,
          priority,
          // 0 means "no override", so the link inherits the destination's own
          // default rather than being pinned to zero.
          buying_price: price || null,
        },
      });
      notifications.show({
        color: 'teal',
        title: `${v.One('destination')} linked`,
        message: `${picked?.name ?? v.One('destination')} added to ${group.name}.`,
      });
      setChosen(null);
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Link failed', message: (e as Error).message });
    }
  }

  return (
    <Modal
      opened={group !== null}
      onClose={onClose}
      size="lg"
      title={group ? `Add ${v.a('destination')} to ${group.name}` : `Add ${v.one('destination')}`}
    >
      <Stack>
        <Select
          label={v.One('destination')}
          placeholder={isPending ? 'Loading…' : 'Choose one'}
          searchable
          data={options.map((d) => ({
            value: d.id,
            label: `${d.name}${d.buyer_name ? ` (${d.buyer_name})` : ''}`,
          }))}
          value={chosen}
          onChange={setChosen}
          nothingFoundMessage={`Every ${v.one('destination')} is already in this ${v.one('routeGroup')}`}
        />

        {picked && (
          <Alert color="gray" variant="light">
            <Text size="sm">
              <Text span ff="monospace">{picked.endpoint_url || `no ${v.p.urlLower}`}</Text>{' '}
              <Badge size="xs" variant="outline" color="gray" ml={4}>
                {picked.delivery_method || picked.method}
              </Badge>
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {picked.link_count === 0
                ? `Not currently used by any ${v.one('routeGroup')}.`
                : `Already used by ${picked.link_count} other ${v.word(picked.link_count, 'routeGroup')}. Its ${v.p.urlLower} and timeout are shared with them.`}
            </Text>
          </Alert>
        )}

        <Grid gutter="sm">
          <Grid.Col span={6}>
            <NumberInput
              label="Price override (£)"
              description={`0 inherits the ${v.one('destination')}'s default`}
              min={0}
              step={0.5}
              decimalScale={2}
              value={price}
              onChange={(v) => setPrice(Number(v) || 0)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput
              label="Priority"
              description="Trial order in priority mode"
              min={0}
              value={priority}
              onChange={(v) => setPriority(Number(v) || 0)}
            />
          </Grid.Col>
        </Grid>

        {price > 0 && (
          <Text size="xs" c="dimmed">
            {`This link will pay ${formatGBP(price)} regardless of the ${v.one('destination')}'s own default.`}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={link.isPending} disabled={!chosen} onClick={submit}>
            Add to group
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
